import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getOfferingById } from '../../api/offerings';
import { getEventRegistrations, getEventWaitlist, cancelEventRegistration, cancelEvent } from '../../api/events';
import Header from '../../Components/Header/Header';
import Sidebar from '../../Components/Sidebar/Sidebar';
import '../../Pages/AdventureShared.css';
import './EventDashboardPage.css';
import ConfirmationModal from '../../Components/Shared/ConfirmationModal';

export default function EventDashboardPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [offering, setOffering] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('attendees');

  // Search filter
  const [searchQuery, setSearchQuery] = useState('');

  // Action states
  const [isExporting, setIsExporting] = useState(false);
  const [processingRegId, setProcessingRegId] = useState(null);
  const [cancelMessage, setCancelMessage] = useState('');
  const [isCancellingEvent, setIsCancellingEvent] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [registrantToCancel, setRegistrantToCancel] = useState(null);
  const [showCancelEventConfirm, setShowCancelEventConfirm] = useState(false);

  const currentUserId = localStorage.getItem("userId") || "9fa85f64-5717-4562-b3fc-2c963f66afa6";

  const fetchDashboardData = async () => {
    try {
      const details = await getOfferingById(id);
      
      // Ensure owner view permission
      const ownerId = details.provider?.providerId || details.providerId;
      if (ownerId !== currentUserId) {
        alert("You are not authorized to manage this event.");
        navigate('/facilitate/offerings');
        return;
      }

      setOffering(details);

      const [regsData, waitlistData] = await Promise.all([
        getEventRegistrations(id),
        getEventWaitlist(id)
      ]);

      setRegistrations(regsData);
      setWaitlist(waitlistData);
    } catch (err) {
      console.error("Failed to load event dashboard data", err);
      alert("Failed to load event dashboard details.");
      navigate('/facilitate/offerings');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [id, navigate]);

  // Remove / Cancel a single customer registration
  const handleRemoveRegistrant = async (regId) => {
    setRegistrantToCancel(regId);
  };

  const handleConfirmRemoveRegistrant = async () => {
    if (!registrantToCancel) return;
    setProcessingRegId(registrantToCancel);
    try {
      await cancelEventRegistration(registrantToCancel);
      setRegistrantToCancel(null);
      await fetchDashboardData(); // Refresh logs and promotion positions
    } catch (err) {
      console.error("Failed to cancel registration", err);
      alert(err.message || "Failed to cancel registration.");
    } finally {
      setProcessingRegId(null);
    }
  };

  // Export Attendees to CSV
  const handleExportCSV = () => {
    setIsExporting(true);
    
    setTimeout(() => {
      try {
        const activeRegs = registrations.filter(r => r.status === 'registered');
        if (activeRegs.length === 0) {
          alert("No registered attendees to export.");
          setIsExporting(false);
          return;
        }

        const headers = ["Registration ID", "Customer Name", "Tickets Booked", "Attendee Names", "Special Requirements", "Registered At"];
        const rows = activeRegs.map(r => [
          r.registrationId,
          r.customerName,
          r.ticketCount,
          r.attendeeNames ? r.attendeeNames.join("; ") : "",
          r.specialRequirements || "",
          r.createdAt ? new Date(r.createdAt).toLocaleString() : ""
        ]);

        const csvContent = "data:text/csv;charset=utf-8," 
          + [headers.join(","), ...rows.map(e => e.map(val => `"${val}"`).join(","))].join("\n");
        
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `attendees_${offering.title.replace(/\s+/g, '_')}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (err) {
        console.error("Failed to export CSV", err);
        alert("Failed to export attendees list.");
      } finally {
        setIsExporting(false);
      }
    }, 800); // Small simulate duration for loader visual feedback
  };

  // Cancel Entire Event
  const handleCancelEventSubmit = (e) => {
    e.preventDefault();
    if (!cancelMessage.trim()) {
      setCancelError("A message to attendees is required to justify cancellation.");
      return;
    }
    setShowCancelEventConfirm(true);
  };

  const handleConfirmCancelEvent = async () => {
    setIsCancellingEvent(true);
    setCancelError('');
    try {
      await cancelEvent(id, cancelMessage);
      setShowCancelEventConfirm(false);
      alert("Event has been cancelled. All registered and waitlisted entries have been updated.");
      navigate('/facilitate/offerings');
    } catch (err) {
      console.error("Failed to cancel event", err);
      setCancelError(err.response?.data?.message || err.message || "Failed to cancel event. Please try again.");
    } finally {
      setIsCancellingEvent(false);
    }
  };

  if (isLoading) {
    return (
      <div className="EventDashboardPage">
        <div className="adventurePageContainer">
          <div className="headerContain"><Header /></div>
          <div className="adventurePageContainer2">
            <div className="sidebarContain"><Sidebar /></div>
            <main className="adv-page-main animate-fade-in">
              <div className="ev-dash-loader-wrap">
                <div className="ev-dash-spinner"></div>
                <p>Loading event manager...</p>
              </div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  // Filters
  const filteredRegs = registrations.filter(r => 
    r.customerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (r.attendeeNames && r.attendeeNames.some(name => name.toLowerCase().includes(searchQuery.toLowerCase())))
  );

  const registeredSpots = registrations
    .filter(r => r.status === 'registered')
    .reduce((sum, r) => sum + r.ticketCount, 0);

  return (
    <div className="EventDashboardPage">
      <div className="adventurePageContainer">
        <div className="headerContain"><Header /></div>
        <div className="adventurePageContainer2">
          <div className="sidebarContain"><Sidebar /></div>

          <main className="adv-page-main animate-fade-in">
            <div className="ev-dash-container">
              {/* Back to offerings link */}
              <button className="off-back-btn" style={{ marginBottom: '16px' }} onClick={() => navigate('/facilitate/offerings')}>
                ← Back to My Offerings
              </button>

              {/* Event Header Panel */}
              <div className="ev-dash-header-panel">
                <div className="ev-dash-header-panel__left">
                  <div className="ev-dash-badge">EVENT MANAGER</div>
                  <h1 className="ev-dash-title">{offering.title}</h1>
                  <p className="ev-dash-meta">
                    📅 {new Date(offering.eventDate).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} at {offering.startTime} ({offering.timezone})
                  </p>
                </div>
                <div className="ev-dash-header-panel__right">
                  <div className="ev-dash-stat-box">
                    <span className="ev-dash-stat-label">Spots Filled</span>
                    <span className="ev-dash-stat-value">{registeredSpots} / {offering.maxCapacity || 'Unlimited'}</span>
                  </div>
                  <div className="ev-dash-stat-box">
                    <span className="ev-dash-stat-label">Waitlisted Queue</span>
                    <span className="ev-dash-stat-value">{waitlist.length} Users</span>
                  </div>
                </div>
              </div>

              {/* Tabs System */}
              <div className="ev-dash-tabs">
                <button 
                  className={`ev-dash-tab-btn ${activeTab === 'attendees' ? 'ev-dash-tab-btn--active' : ''}`}
                  onClick={() => setActiveTab('attendees')}
                >
                  Attendees Log ({registrations.filter(r => r.status === 'registered').length})
                </button>
                <button 
                  className={`ev-dash-tab-btn ${activeTab === 'waitlist' ? 'ev-dash-tab-btn--active' : ''}`}
                  onClick={() => setActiveTab('waitlist')}
                >
                  Waitlist Queue ({waitlist.length})
                </button>
                <button 
                  className={`ev-dash-tab-btn ${activeTab === 'settings' ? 'ev-dash-tab-btn--active' : ''}`}
                  onClick={() => setActiveTab('settings')}
                >
                  Event Cancellation
                </button>
              </div>

              {/* Tab Contents */}
              <div className="ev-dash-content">
                
                {/* TAB 1: ATTENDEES LOG */}
                {activeTab === 'attendees' && (
                  <div className="ev-dash-pane animate-fade-in">
                    <div className="ev-dash-pane__header">
                      <div className="ev-dash-search-box">
                        🔍 
                        <input 
                          type="text" 
                          placeholder="Search attendees by name..." 
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </div>
                      <button 
                        className="ev-dash-btn ev-dash-btn--export"
                        disabled={isExporting}
                        onClick={handleExportCSV}
                      >
                        {isExporting ? (
                          <>
                            <div className="ev-dash-btn-spinner"></div>
                            Exporting CSV...
                          </>
                        ) : 'Export Attendees (CSV)'}
                      </button>
                    </div>

                    <div className="ev-dash-table-wrapper">
                      <table className="ev-dash-table">
                        <thead>
                          <tr>
                            <th>Booked By</th>
                            <th>Status</th>
                            <th>Tickets</th>
                            <th>Attendee Names</th>
                            <th>Requirements</th>
                            <th>Booked At</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredRegs.length === 0 ? (
                            <tr>
                              <td colSpan="7" className="ev-dash-table-empty">
                                {searchQuery ? "No matching registrations found." : "No registrations recorded for this event."}
                              </td>
                            </tr>
                          ) : (
                            filteredRegs.map((reg) => (
                              <tr key={reg.registrationId} className={reg.status === 'cancelled' ? 'ev-dash-row--cancelled' : ''}>
                                <td>
                                  <strong>{reg.customerName || 'Anonymous'}</strong>
                                </td>
                                <td>
                                  <span className={`ev-badge-status ev-badge-status--${reg.status}`}>
                                    {reg.status.toUpperCase()}
                                  </span>
                                </td>
                                <td>{reg.ticketCount}</td>
                                <td>{reg.attendeeNames ? reg.attendeeNames.join(", ") : "N/A"}</td>
                                <td>
                                  <span className="ev-dash-reqs-text" title={reg.specialRequirements}>
                                    {reg.specialRequirements || "—"}
                                  </span>
                                </td>
                                <td>{new Date(reg.createdAt).toLocaleDateString()}</td>
                                <td>
                                  {reg.status === 'registered' && (
                                    <button 
                                      className="ev-dash-btn ev-dash-btn--danger-sm"
                                      disabled={processingRegId !== null}
                                      onClick={() => handleRemoveRegistrant(reg.registrationId)}
                                    >
                                      {processingRegId === reg.registrationId ? (
                                        <div className="ev-dash-btn-spinner"></div>
                                      ) : 'Cancel Ticket'}
                                    </button>
                                  )}
                                  {reg.status === 'cancelled' && (
                                    <span style={{ fontSize: '11px', color: '#666' }}>
                                      Cancelled {reg.cancelledAt ? new Date(reg.cancelledAt).toLocaleDateString() : ''}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* TAB 2: WAITLIST QUEUE */}
                {activeTab === 'waitlist' && (
                  <div className="ev-dash-pane animate-fade-in">
                    <h3 className="ev-dash-pane-title">Waitlisted Entries Queue</h3>
                    <p style={{ color: '#aaa', fontSize: '13px', marginBottom: '20px' }}>
                      Waitlisted users will be automatically promoted to Registered status in queue order if active registrations are cancelled.
                    </p>

                    <div className="ev-dash-waitlist-list">
                      {waitlist.length === 0 ? (
                        <div className="ev-dash-pane-empty">
                          <span style={{ fontSize: '32px' }}>👥</span>
                          <h4>Waitlist is empty</h4>
                          <p>All customers were able to register successfully.</p>
                        </div>
                      ) : (
                        waitlist.map((entry) => (
                          <div key={entry.waitlistId} className="ev-waitlist-card">
                            <div className="ev-waitlist-card__pos">#{entry.position}</div>
                            <div className="ev-waitlist-card__details">
                              <h4>{entry.customerName || 'Anonymous Customer'}</h4>
                              <p>Joined Queue: {new Date(entry.createdAt).toLocaleString()}</p>
                            </div>
                            <div className="ev-waitlist-card__actions">
                              <button 
                                className="ev-dash-btn ev-dash-btn--danger-sm"
                                disabled={processingRegId !== null}
                                onClick={async () => {
                                  // Waitlist entries map to registrations. Let's find the registration for this user
                                  const reg = registrations.find(r => r.customerId === entry.customerId && r.status === 'waitlisted');
                                  if (reg) {
                                    handleRemoveRegistrant(reg.registrationId);
                                  } else {
                                    alert("Could not locate registration for waitlisted user.");
                                  }
                                }}
                              >
                                {processingRegId !== null ? <div className="ev-dash-btn-spinner"></div> : 'Cancel Place'}
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* TAB 3: CANCELLATION SETTINGS */}
                {activeTab === 'settings' && (
                  <div className="ev-dash-pane animate-fade-in" style={{ maxWidth: '600px' }}>
                    <h3 className="ev-dash-pane-title" style={{ color: '#ff6b6b' }}>Danger Zone — Cancel Event</h3>
                    <p style={{ color: '#ccc', fontSize: '13.5px', lineHeight: '1.5', marginBottom: '20px' }}>
                      Cancelling this event will set its visibility to **Draft**, release all booked slots, cancel all user registrations, and automatically notify all registered and waitlisted customers. This action cannot be undone.
                    </p>

                    <form onSubmit={handleCancelEventSubmit} className="ev-dash-cancel-form">
                      {cancelError && (
                        <div style={{ color: '#ff6b6b', backgroundColor: 'rgba(255, 107, 107, 0.1)', border: '1px solid rgba(255, 107, 107, 0.3)', padding: '12px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' }}>
                          ⚠️ {cancelError}
                        </div>
                      )}

                      <div className="ev-dash-form-group">
                        <label>Justification Message to Attendees <span className="ev-required">*</span></label>
                        <textarea 
                          required
                          placeholder="Provide the reason for event cancellation. This will be sent as a notification to all registered attendees..."
                          value={cancelMessage}
                          onChange={(e) => setCancelMessage(e.target.value)}
                          maxLength={500}
                        />
                        <span className="ev-char-count">{500 - cancelMessage.length} characters remaining</span>
                      </div>

                      <button 
                        type="submit" 
                        className="ev-dash-btn ev-dash-btn--danger"
                        disabled={isCancellingEvent}
                      >
                        {isCancellingEvent ? (
                          <>
                            <div className="ev-dash-btn-spinner" style={{ borderTopColor: '#fff' }}></div>
                            Cancelling Event...
                          </>
                        ) : 'Confirm Event Cancellation 🚫'}
                      </button>
                    </form>
                  </div>
                )}

              </div>
            </div>
          </main>
        </div>
      </div>

      <ConfirmationModal
        isOpen={registrantToCancel !== null}
        onClose={() => setRegistrantToCancel(null)}
        onConfirm={handleConfirmRemoveRegistrant}
        title="Cancel Registration?"
        message="Are you sure you want to cancel this registration? This will automatically promote waitlisted users if capacity becomes available."
        confirmText="Yes, Cancel Registration"
        cancelText="Go Back"
        isDanger={true}
        isLoading={processingRegId !== null}
      />

      <ConfirmationModal
        isOpen={showCancelEventConfirm}
        onClose={() => setShowCancelEventConfirm(false)}
        onConfirm={handleConfirmCancelEvent}
        title="Cancel Entire Event?"
        message="WARNING: Are you sure you want to cancel this event? This will mark the listing as Draft, release all spots, cancel all tickets, and notify customers. This action is irreversible."
        confirmText="Yes, Cancel Event"
        cancelText="No, Keep Event"
        isDanger={true}
        isLoading={isCancellingEvent}
      />
    </div>
  );
}
